/**
 * Pure helpers behind the Access tab: alias-list parsing, the "last changed" cell, and the
 * multi-industry grant run.
 */

import type { IndustryGrantee, IndustryGrants } from '../../client/IvtHubClient';

/** CreateIndustryGrants' per-call bound on `aliases`. */
export const MAX_ALIASES_PER_CALL = 100;

/**
 * A pasted list of aliases → unique lower-case aliases, in first-seen order. Commas,
 * semicolons, whitespace and newlines all separate, including the full-width / ideographic
 * forms and zero-width spaces a Chinese document carries. The service stores aliases
 * lower-case.
 */
export function parseAliases(text: string): string[] {
  const seen = new Set<string>();
  for (const raw of text.split(/[\s,，、;；\u200b]+/)) {
    const a = raw.trim().toLowerCase();
    if (a) seen.add(a);
  }
  return [...seen];
}

/** The most recent grant on the industry, or undefined when nobody is granted. */
export function lastGrant(grantees: readonly IndustryGrantee[] | undefined): IndustryGrantee | undefined {
  let latest: IndustryGrantee | undefined;
  for (const g of grantees ?? []) {
    if (!latest || (g.grantedAt?.getTime() ?? 0) > (latest.grantedAt?.getTime() ?? 0)) latest = g;
  }
  return latest;
}

/** `YYYY-MM-DD HH:mm`, UTC — the same rendering as the page header. */
export function formatTimestamp(d: Date | undefined): string {
  return d ? d.toISOString().slice(0, 16).replace('T', ' ') : '';
}

export type GrantOutcome = { ok: true } | { ok: false; error: unknown };

export interface GrantRun {
  /** Per industry, except those rejected as invalid input. */
  outcomes: Map<string, GrantOutcome>;
  /** Distinct InvalidInputException messages. */
  unknownAliases: string[];
}

const isInvalidInput = (e: unknown) =>
  (e as { name?: string } | null)?.name === 'InvalidInputException';

/**
 * Grant `aliases` to every industry at once.
 *
 * Concurrent is safe because each call is all-or-nothing and input validation is the same for
 * every industry: an invalid alias fails every call and nothing is written anywhere. Any other
 * failure is per-industry, and since already-granted aliases are no-ops, re-running the
 * failed ones is safe.
 */
export async function grantConcurrently(
  industries: readonly string[],
  aliases: string[],
  create: (industry: string, aliases: string[]) => Promise<IndustryGrants>,
): Promise<GrantRun> {
  const settled = await Promise.allSettled(industries.map((i) => create(i, aliases)));
  const outcomes = new Map<string, GrantOutcome>();
  const unknown = new Set<string>();
  settled.forEach((r, idx) => {
    if (r.status === 'fulfilled') outcomes.set(industries[idx], { ok: true });
    else if (isInvalidInput(r.reason)) unknown.add((r.reason as Error).message);
    else outcomes.set(industries[idx], { ok: false, error: r.reason });
  });
  return { outcomes, unknownAliases: [...unknown] };
}
